import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PostingRecoveryContent } from '@shared/posting/PostingRecoveryContent';
import type { PostingRecoveryView } from '@shared/posting/postingRecovery';
import { FLOATING_PANEL_HOST_ID } from '../lib/floatingPanelHost';
import { useNotificationStore } from '../store/notificationStore';

/**
 * N8 (86d3w2kez) part 2 — the recovery prompt a deep-linked arrival raises.
 *
 * <b>⛔ ANCHORED BOTTOM-LEFT — OWNER RULING 2026-08-24, and it is not a taste call.</b> On the MIS and
 * IR detail pages the right edge belongs to the E9 activity dock: a permanent rail, plus a 364px panel
 * that is static at ≥lg and becomes an `absolute inset-y-0 right-10 z-30` OVERLAY below it
 * (`ActivityDock.tsx:227`). A bottom-right panel is clear at desktop width and then vanishes behind
 * that overlay on a laptop — which is exactly the moment someone is being told a posting failed. Left
 * is clear at every breakpoint, and `z-40` keeps it above the dock's 30 when they do meet.
 *
 * <b>Fed by props, never by its own fetch.</b> The page already holds these jobs — SO/CL an
 * `OutboxPostingStatusDto`, MIS its `activePostingBatches`, IR the flat `doc.posting*` fields. A
 * second fetch here would drift from the inline card it is meant to mirror, and the two would
 * disagree on screen at the same time.
 *
 * <b>It auto-resolves.</b> There is no "it worked" state to dismiss: when the parent re-renders with
 * no failed job left — which an SSE settle causes — the panel simply stops rendering.
 */
export function FloatingPostingRecoveryPanel({
  views,
  onRetry,
  onReconcile,
  onCancel,
  isBusy = false,
}: {
  /** Every posting job the parent knows about. Settled ones are filtered out here. */
  views: PostingRecoveryView[];
  onRetry?: (view: PostingRecoveryView) => void;
  onReconcile?: (view: PostingRecoveryView) => void;
  onCancel?: (view: PostingRecoveryView) => void;
  isBusy?: boolean;
}) {
  const [dismissed, setDismissed] = useState(false);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const dispatch = useRecoveryDispatch();

  // Resolved in an effect rather than during render: on the first pass this component may run before
  // AppLayout has committed its column to the DOM, and reading it in render would cache the miss.
  useEffect(() => {
    setHost(document.getElementById(FLOATING_PANEL_HOST_ID));
  }, []);

  const failed = views.filter((v) => v.status === 'Failed');

  // Re-arm the panel whenever a NEW failure appears, so dismissing one prompt does not silence the
  // next. Keyed on the job ids rather than the count — one failing while another is cancelled leaves
  // the count unchanged.
  const signature = failed.map((v) => v.jobId ?? (v.sourceId != null ? `s${v.sourceId}` : -1)).join(",");
  useEffect(() => {
    setDismissed(false);
  }, [signature]);

  if (dismissed || failed.length === 0 || !host) return null;

  return createPortal(
    <div className="pointer-events-none absolute inset-x-4 bottom-4 z-40 flex justify-start">
      <div className="pointer-events-auto w-full max-w-sm space-y-3 rounded-lg border border-destructive/30 bg-card p-4 shadow-lg">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-foreground">
            {failed.length === 1 ? 'This posting needs you' : `${failed.length} postings need you`}
          </p>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="-mr-1 -mt-1 h-6 w-6"
                aria-label="Dismiss recovery panel"
                onClick={() => setDismissed(true)}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Dismiss — the card on the page stays</TooltipContent>
          </Tooltip>
        </div>

        {/* MIS is the multi-row case: several batches can fail independently, and each keeps its own
            identity so a retry targets the right batchId. Stacked, not merged into a count. */}
        <div className="max-h-96 space-y-4 overflow-y-auto">
          {failed.map((view) => (
            <PostingRecoveryContent
              key={view.jobId ?? view.subLabel ?? 'single'}
              view={view}
              isBusy={isBusy}
              // ⛔ Through N4's GENERIC endpoint wherever the centre knows this job, falling back to
              // the page's own per-flow handler where it does not — see `useRecoveryDispatch`.
              onRetry={() => dispatch('retry', view.jobId, onRetry && (() => onRetry(view)))}
              onReconcile={() =>
                dispatch('reconcile', view.jobId, onReconcile && (() => onReconcile(view)))
              }
              onCancel={
                onCancel ? () => dispatch('cancel', view.jobId, () => onCancel(view)) : null
              }
            />
          ))}
        </div>
      </div>
    </div>,
    host,
  );
}

/**
 * Dispatches a recovery verb through N4's GENERIC endpoint when the notification centre knows about
 * this job, and through the page's own handler when it does not.
 *
 * ⚠️ N4's endpoints are keyed by NOTIFICATION id while every posting surface thinks in JOB ids, so
 * something has to bridge the two. Preferring N4 is what the ticket asks for, and the reason is that
 * the generic path is reach-gated server-side: a stale click comes back as a 400 carrying the
 * CORRECTED row, which the store splices in — so the user sees the right buttons instead of pressing
 * the wrong one again.
 *
 * ⛔ The fallback is not decoration. MIS carries no outbox job id on the wire, and a posting that
 * settled before the notification centre shipped has no row to act through — on those paths the
 * page's own per-flow endpoint is the only one that exists. It is reach-gated too.
 */
function useRecoveryDispatch() {
  const retry = useNotificationStore((s) => s.retry);
  const cancel = useNotificationStore((s) => s.cancel);
  const reconcile = useNotificationStore((s) => s.reconcile);

  return function dispatch(
    verb: 'retry' | 'cancel' | 'reconcile',
    jobId: number | null,
    fallback: (() => void) | undefined,
  ) {
    const store = useNotificationStore.getState();
    const match =
      jobId === null
        ? undefined
        : store.items.find((n) => n.jobId === jobId) ?? store.pinned.find((n) => n.jobId === jobId);

    if (!match) {
      fallback?.();
      return;
    }

    const action = verb === 'retry' ? retry : verb === 'cancel' ? cancel : reconcile;
    void action(match.id)
      .then((refusal) => {
        // A refusal is N4 telling us the row we acted on was stale; the store has already replaced it
        // with the server's corrected copy. Surfacing the explanation is the point — swallowing it
        // leaves the user staring at a row that just changed under them for no stated reason.
        if (refusal) toast.warning(refusal);
      })
      .catch(() => toast.error('Could not reach the server. Nothing was changed — try again.'));
  };
}
